import { useInfiniteQuery } from "@tanstack/react-query";
import { apiClient } from "../lib/api-client";
import type { MyCandidateFromAPI } from "../types/candidate";

const PAGE_LIMIT = 20;

interface MyCandidatesResponse {
  success: boolean;
  data: {
    items: MyCandidateFromAPI[];
    total: number;
    page: number;
    limit: number;
  };
  message: string;
}

interface UseCandidateDataReturn {
  candidates: MyCandidateFromAPI[];
  total: number;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  error: string | null;
  refetch: () => void;
}

export function useCandidateData(
  jdPath: string | null
): UseCandidateDataReturn {
  const query = useInfiniteQuery({
    queryKey: ["candidates", "my-candidates", jdPath] as const,
    queryFn: ({ signal, pageParam }) =>
      apiClient.get<MyCandidatesResponse>(
        `/candidates/my-candidates?jd_path=${encodeURIComponent(jdPath ?? "")}&page=${pageParam}&limit=${PAGE_LIMIT}`,
        { signal }
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { total, page, limit } = lastPage.data;
      const fetched = page * limit;
      return fetched < total ? page + 1 : undefined;
    },
    enabled: !!jdPath,
  });

  // Flatten all pages into a single candidates array
  const candidates = query.data?.pages.flatMap((page) => page.data.items) ?? [];

  const total = query.data?.pages[0]?.data.total ?? 0;

  return {
    candidates,
    total,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error ? "Failed to load candidates." : null,
    refetch: query.refetch,
  };
}
